/**
 * BookingsKpiCell — one cell in {@link BookingsKpiRow} (BON-490). A real
 * `<button>` when it has an `onClick` (Bookings/Reservations/Tree
 * replacements), so keyboard users get a focusable control with the
 * `:focus-visible` ring (`.nb-booking-kpi-cell:focus-visible` in
 * dashboards.css) and — for the seat cells — the same hover-highlight on
 * focus (`onFocus`/`onBlur` mirror `onHover`/`onLeave`). Cells with no
 * `onClick` (In checkout) render a plain, non-focusable `<div>` instead: a
 * focusable dead control is worse than an inert one.
 */
import type { CSSProperties } from 'react';

import type { FillSegment } from '../../primitives';

import { BookingsKpiCellContent } from './BookingsKpiCellContent';

export interface BookingsKpiCellProps {
  label: string;
  value: number;
  sub: string;
  /** The matching fill-bar lane, painted as a small color-key dot. Omitted for
   *  tree replacements — it isn't a bar segment. */
  tone?: FillSegment['tone'];
  active?: boolean;
  /** Dimmed while a DIFFERENT cell's tone is highlighted. */
  dim?: boolean;
  /** Tree replacements paint filled (brand primary) — the one count that isn't
   *  part of capacity and needs to be noticed on sight. */
  filled?: boolean;
  /** Offsets the cell past the divider (desktop/tablet only). */
  fenced?: boolean;
  /** Mobile-only: spans both grid columns (In checkout with no TR cell). */
  fullRow?: boolean;
  /** Mobile cells sit on the grid's own border color, so non-filled tiles need
   *  an explicit `--nb-bg` fill to read as tiles (review fix 7). */
  isMobile?: boolean;
  onClick?: () => void;
  onHover?: () => void;
  onLeave?: () => void;
  /** Keyboard parity for `onHover`/`onLeave` (BON-490 a11y review) — only
   *  meaningful on a clickable (focusable) cell. */
  onFocus?: () => void;
  onBlur?: () => void;
}

/** Idle-state fill: mobile tiles need an explicit `--nb-bg` fill to read
 *  against the grid's border-colored background; desktop/tablet stays
 *  transparent against the card (review fix 7). */
function idleCellBackground(isMobile?: boolean): string {
  return isMobile ? 'var(--nb-bg)' : 'transparent';
}

/** Button/div chrome, isolated from the component so its branching doesn't
 *  count against `BookingsKpiCell`'s own complexity/line caps. */
function cellStyle(opts: {
  filled?: boolean;
  active?: boolean;
  dim?: boolean;
  fenced?: boolean;
  fullRow?: boolean;
  isMobile?: boolean;
  clickable: boolean;
}): CSSProperties {
  const { filled, active, dim, fenced, fullRow, isMobile, clickable } = opts;
  return {
    display: 'block',
    textAlign: 'left',
    padding: '9px 12px',
    border: 0,
    borderRadius: 9,
    gridColumn: fullRow ? '1 / -1' : undefined,
    marginLeft: fenced ? 8 : 0,
    cursor: clickable ? 'pointer' : 'default',
    background: filled
      ? 'var(--nb-primary)'
      : active
        ? 'var(--nb-surface)'
        : idleCellBackground(isMobile),
    boxShadow: filled
      ? active
        ? '0 0 0 2px var(--nb-primary-soft)'
        : 'none'
      : active
        ? 'inset 0 0 0 1px var(--nb-border-2)'
        : 'none',
    opacity: dim ? 0.5 : 1,
    transition: 'background .12s, opacity .12s, box-shadow .12s',
  };
}

export function BookingsKpiCell({
  label,
  value,
  sub,
  tone,
  active,
  dim,
  filled,
  fenced,
  fullRow,
  isMobile,
  onClick,
  onHover,
  onLeave,
  onFocus,
  onBlur,
}: BookingsKpiCellProps) {
  const testId = `kpi-cell-${label}`;
  const style = cellStyle({
    filled,
    active,
    dim,
    fenced,
    fullRow,
    isMobile,
    clickable: Boolean(onClick),
  });
  const content = (
    <BookingsKpiCellContent label={label} value={value} sub={sub} tone={tone} filled={filled} />
  );

  if (!onClick) {
    // No tab to select → not a control. A focusable dead button is worse
    // than an inert div; hover-highlight for mouse users still works on a div.
    return (
      <div data-testid={testId} onMouseEnter={onHover} onMouseLeave={onLeave} style={style}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="nb-booking-kpi-cell"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      style={style}
    >
      {content}
    </button>
  );
}
